import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dropdown, Form } from 'react-bootstrap'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { formatDate } from '../../utils/dateFormatter'
import ClearableBadge from './ClearableBadge'
import './DatesDropdown.scss'

export interface RelativeDate {
  id: number
  name: string
  date: number
}

export enum RelativeDateEnum {
  WITHIN_1_WEEK = 1,
  WITHIN_1_MONTH = 2,
  WITHIN_3_MONTHS = 3,
  WITHIN_1_YEAR = 4,
  THIS_YEAR = 5,
  THIS_MONTH = 6,
  TODAY = 7,
  YESTERDAY = 8,
}

// Mapping for query strings (same as React)
export const RELATIVE_DATE_QUERYSTRINGS = [
  {
    relativeDate: RelativeDateEnum.WITHIN_1_WEEK,
    dateQuery: '-1 week to now',
    isRange: true,
  },
  {
    relativeDate: RelativeDateEnum.WITHIN_1_MONTH,
    dateQuery: '-1 month to now',
    isRange: true,
  },
  {
    relativeDate: RelativeDateEnum.WITHIN_3_MONTHS,
    dateQuery: '-3 month to now',
    isRange: true,
  },
  {
    relativeDate: RelativeDateEnum.WITHIN_1_YEAR,
    dateQuery: '-1 year to now',
    isRange: true,
  },
  {
    relativeDate: RelativeDateEnum.THIS_YEAR,
    dateQuery: 'this year',
    isRange: false,
  },
  {
    relativeDate: RelativeDateEnum.THIS_MONTH,
    dateQuery: 'this month',
    isRange: false,
  },
  {
    relativeDate: RelativeDateEnum.TODAY,
    dateQuery: 'today',
    isRange: false,
  },
  {
    relativeDate: RelativeDateEnum.YESTERDAY,
    dateQuery: 'yesterday',
    isRange: false,
  },
]

const getRelativeDates = (t: (key: string) => string): RelativeDate[] => {
  const now = new Date()
  return [
    {
      id: RelativeDateEnum.WITHIN_1_WEEK,
      name: t('within_1_week'),
      date: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).getTime(),
    },
    {
      id: RelativeDateEnum.WITHIN_1_MONTH,
      name: t('within_1_month'),
      date: new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()).getTime(),
    },
    {
      id: RelativeDateEnum.WITHIN_3_MONTHS,
      name: t('within_3_months'),
      date: new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()).getTime(),
    },
    {
      id: RelativeDateEnum.WITHIN_1_YEAR,
      name: t('within_1_year'),
      date: new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).getTime(),
    },
    {
      id: RelativeDateEnum.THIS_YEAR,
      name: t('this_year'),
      date: new Date(now.getFullYear(), 0, 1).getTime(),
    },
    {
      id: RelativeDateEnum.THIS_MONTH,
      name: t('this_month'),
      date: new Date(now.getFullYear(), now.getMonth(), 1).getTime(),
    },
    {
      id: RelativeDateEnum.TODAY,
      name: t('today'),
      date: new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime(),
    },
    {
      id: RelativeDateEnum.YESTERDAY,
      name: t('yesterday'),
      date: new Date(now.getTime() - 24 * 60 * 60 * 1000).getTime(),
    },
  ]
}

interface DatesDropdownProps {
  title: string
  icon: string
  createdDateTo?: string
  createdDateFrom?: string
  addedDateTo?: string
  addedDateFrom?: string
  createdRelativeDate?: RelativeDate
  addedRelativeDate?: RelativeDate
  onDatesSet: () => void
  onCreatedDateToChange: (date: string) => void
  onCreatedDateFromChange: (date: string) => void
  onAddedDateToChange: (date: string) => void
  onAddedDateFromChange: (date: string) => void
  onCreatedRelativeDateChange: (date: RelativeDate | undefined) => void
  onAddedRelativeDateChange: (date: RelativeDate | undefined) => void
  disabled?: boolean
  className?: string
  fullWidth?: boolean
}

export const DatesDropdown: React.FC<DatesDropdownProps> = ({
  title,
  icon,
  createdDateTo = '',
  createdDateFrom = '',
  addedDateTo = '',
  addedDateFrom = '',
  createdRelativeDate,
  addedRelativeDate,
  onDatesSet,
  onCreatedDateToChange,
  onCreatedDateFromChange,
  onAddedDateToChange,
  onAddedDateFromChange,
  onCreatedRelativeDateChange,
  onAddedRelativeDateChange,
  disabled = false,
  className = '',
  fullWidth = true,
}) => {
  const { t } = useTranslation()
  const [show, setShow] = useState(false)
  const getInitialDropDirection = () => {
    if (typeof window === 'undefined') return 'down'
    return window.innerWidth >= 992 ? 'end' : 'down'
  }
  const [dropDirection, setDropDirection] = useState<'down' | 'end'>(getInitialDropDirection)
  const [overlayContainer, setOverlayContainer] = useState<HTMLElement | null>(null)
  const toggleRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 360 })

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setOverlayContainer(document.body)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mediaQuery = window.matchMedia('(min-width: 992px)')
    const updateDirection = () => setDropDirection(mediaQuery.matches ? 'end' : 'down')
    updateDirection()
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateDirection)
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(updateDirection)
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateDirection)
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(updateDirection)
      }
    }
  }, [])

  const popperConfig = useMemo(
    () => ({
      strategy: 'fixed' as const,
      modifiers: [
        { name: 'applyStyles', enabled: false },
        { name: 'eventListeners', enabled: false },
      ],
    }),
    []
  )

  const updateMenuPosition = useCallback(() => {
    if (typeof window === 'undefined') return
    const buttonRect = toggleRef.current?.getBoundingClientRect()
    if (!buttonRect) return

    const panel = document.querySelector('.documents-filters-panel') as HTMLElement | null
    const panelRect = panel?.getBoundingClientRect()
    const spacing = 16
    const viewportWidth = window.innerWidth
    const isDesktop = viewportWidth >= 992

    let width = Math.min(420, viewportWidth - spacing * 2)
    let left = isDesktop && panelRect ? panelRect.right + spacing : spacing

    if (left + width > viewportWidth - spacing) {
      left = viewportWidth - width - spacing
    }
    if (left < spacing) {
      left = spacing
    }

    const top = buttonRect.top + window.scrollY

    setMenuPosition({
      top,
      left: left + window.scrollX,
      width,
    })
  }, [])

  useEffect(() => {
    if (!show) return
    updateMenuPosition()
    const handleUpdate = () => updateMenuPosition()
    window.addEventListener('resize', handleUpdate)
    window.addEventListener('scroll', handleUpdate, true)
    return () => {
      window.removeEventListener('resize', handleUpdate)
      window.removeEventListener('scroll', handleUpdate, true)
    }
  }, [show, updateMenuPosition])

  const relativeDates = getRelativeDates(t)

  const isActive = () => {
    return !!(createdRelativeDate || createdDateFrom || createdDateTo || addedRelativeDate || addedDateFrom || addedDateTo)
  }

  const getActiveCount = (): number => {
    let count = 0
    // Count relative dates
    if (createdRelativeDate) count++
    if (addedRelativeDate) count++
    // Count manual dates (only if no relative date for that type)
    if (!createdRelativeDate && (createdDateFrom || createdDateTo)) count++
    if (!addedRelativeDate && (addedDateFrom || addedDateTo)) count++
    return count
  }

  const getButtonVariant = (): string => {
    return isActive() ? 'primary' : 'outline-primary'
  }

  const formatDateForDisplay = (date: number): string => {
    return formatDate(new Date(date).toISOString())
  }

  const handleCreatedRelativeDateChange = (rd: RelativeDate | undefined) => {
    onCreatedRelativeDateChange(rd)
    // Don't call onDatesSet() here - let useEffect in FilterEditor handle it
  }

  const handleAddedRelativeDateChange = (rd: RelativeDate | undefined) => {
    onAddedRelativeDateChange(rd)
    // Don't call onDatesSet() here - let useEffect in FilterEditor handle it
  }

  const reset = () => {
    onCreatedDateFromChange('')
    onCreatedDateToChange('')
    onAddedDateFromChange('')
    onAddedDateToChange('')
    onCreatedRelativeDateChange(undefined)
    onAddedRelativeDateChange(undefined)
    onDatesSet()
  }

  return (
    <Dropdown
      show={show}
      onToggle={(nextShow) => {
        if (nextShow) {
          updateMenuPosition()
        }
        setShow(nextShow)
      }}
      className={[className, fullWidth ? 'w-100' : ''].filter(Boolean).join(' ')}
      drop={dropDirection}
      autoClose="outside"
    >
      <Dropdown.Toggle
        variant={getButtonVariant()}
        size="sm"
        disabled={disabled}
        className={`${fullWidth ? 'w-100 ' : ''}d-flex align-items-center justify-content-between text-start`}
        style={{ whiteSpace: 'nowrap' }}
        ref={toggleRef}
      >
        <span className="filter-chip__label">
          <i className={`bi bi-${icon}`}></i>
          <span>{title}</span>
        </span>
        {getActiveCount() > 0 && <ClearableBadge number={getActiveCount()} selected={isActive()} onCleared={reset} />}
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="shadow date-dropdown p-2 filter-dropdown-menu"
        ref={menuRef}
        style={{
          position: 'fixed',
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`,
          width: `${menuPosition.width}px`,
          minWidth: `${menuPosition.width}px`,
          transform: 'none',
        }}
        popperConfig={popperConfig}
      >
        {/* Created Section */}
        <h6 className="dropdown-header border-bottom">{t('created')}</h6>
        <div className="list-group list-group-flush">
          {/* Created Relative Date */}
          <div className="list-group-item d-flex p-2" role="menuitem">
            <div className="selected-icon" style={{ width: '24px', flexShrink: 0 }}>
              {createdRelativeDate && (
                <a
                  href="#"
                  className="text-decoration-none"
                  onClick={(e) => {
                    e.preventDefault()
                    handleCreatedRelativeDateChange(undefined)
                  }}
                >
                  <i className="bi bi-check text-dark"></i>
                </a>
              )}
            </div>
            <div className="flex-grow-1 ps-1 pe-2">
              <Form.Select
                size="sm"
                value={createdRelativeDate?.id || ''}
                onChange={(e) => {
                  const selectedDate = relativeDates.find((d) => d.id === parseInt(e.target.value))
                  handleCreatedRelativeDateChange(selectedDate)
                }}
                disabled={disabled}
              >
                <option value="">{t('relative_dates')}</option>
                {relativeDates.map((date) => (
                  <option key={date.id} value={date.id}>
                    {date.name} ({formatDateForDisplay(date.date)} – {t('now')})
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>

          {/* Created From */}
          <div className="list-group-item d-flex p-2" role="menuitem">
            <div className="selected-icon" style={{ width: '24px', flexShrink: 0 }}>
              {createdDateFrom && (
                <a
                  href="#"
                  className="text-decoration-none"
                  onClick={(e) => {
                    e.preventDefault()
                    onCreatedDateFromChange('')
                    onDatesSet()
                  }}
                >
                  <i className="bi bi-check"></i>
                </a>
              )}
            </div>
            <div className="input-group input-group-sm ps-1 pe-2">
              <span className="input-group-text small text-muted" style={{ width: '60px' }}>
                {t('from')}
              </span>
              <Form.Control
                type="date"
                size="sm"
                value={createdDateFrom}
                onChange={(e) => {
                  // Clear relative date when manual date is entered
                  if (e.target.value && createdRelativeDate) {
                    onCreatedRelativeDateChange(undefined)
                  }
                  onCreatedDateFromChange(e.target.value)
                  onDatesSet()
                }}
                disabled={disabled}
              />
            </div>
          </div>

          {/* Created To */}
          <div className="list-group-item d-flex p-2" role="menuitem">
            <div className="selected-icon" style={{ width: '24px', flexShrink: 0 }}>
              {createdDateTo && (
                <a
                  href="#"
                  className="text-decoration-none"
                  onClick={(e) => {
                    e.preventDefault()
                    onCreatedDateToChange('')
                    onDatesSet()
                  }}
                >
                  <i className="bi bi-check"></i>
                </a>
              )}
            </div>
            <div className="input-group input-group-sm ps-1 pe-2">
              <span className="input-group-text small text-muted" style={{ width: '60px' }}>
                {t('to')}
              </span>
              <Form.Control
                type="date"
                size="sm"
                value={createdDateTo}
                onChange={(e) => {
                  // Clear relative date when manual date is entered
                  if (e.target.value && createdRelativeDate) {
                    onCreatedRelativeDateChange(undefined)
                  }
                  onCreatedDateToChange(e.target.value)
                  onDatesSet()
                }}
                disabled={disabled}
              />
            </div>
          </div>
        </div>

        {/* Added Section */}
        <h6 className="dropdown-header border-bottom">{t('added')}</h6>
        <div className="list-group list-group-flush">
          {/* Added Relative Date */}
          <div className="list-group-item d-flex p-2" role="menuitem">
            <div className="selected-icon" style={{ width: '24px', flexShrink: 0 }}>
              {addedRelativeDate && (
                <a
                  href="#"
                  className="text-decoration-none"
                  onClick={(e) => {
                    e.preventDefault()
                    handleAddedRelativeDateChange(undefined)
                  }}
                >
                  <i className="bi bi-check text-dark"></i>
                </a>
              )}
            </div>
            <div className="flex-grow-1 ps-1 pe-2">
              <Form.Select
                size="sm"
                value={addedRelativeDate?.id || ''}
                onChange={(e) => {
                  const selectedDate = relativeDates.find((d) => d.id === parseInt(e.target.value))
                  handleAddedRelativeDateChange(selectedDate)
                }}
                disabled={disabled}
              >
                <option value="">{t('relative_dates')}</option>
                {relativeDates.map((date) => (
                  <option key={date.id} value={date.id}>
                    {date.name} ({formatDateForDisplay(date.date)} – {t('now')})
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>

          {/* Added From */}
          <div className="list-group-item d-flex p-2" role="menuitem">
            <div className="selected-icon" style={{ width: '24px', flexShrink: 0 }}>
              {addedDateFrom && (
                <a
                  href="#"
                  className="text-decoration-none"
                  onClick={(e) => {
                    e.preventDefault()
                    onAddedDateFromChange('')
                    onDatesSet()
                  }}
                >
                  <i className="bi bi-check"></i>
                </a>
              )}
            </div>
            <div className="input-group input-group-sm ps-1 pe-2">
              <span className="input-group-text small text-muted" style={{ width: '60px' }}>
                {t('from')}
              </span>
              <Form.Control
                type="date"
                size="sm"
                value={addedDateFrom}
                onChange={(e) => {
                  // Clear relative date when manual date is entered
                  if (e.target.value && addedRelativeDate) {
                    onAddedRelativeDateChange(undefined)
                  }
                  onAddedDateFromChange(e.target.value)
                  onDatesSet()
                }}
                disabled={disabled}
              />
            </div>
          </div>

          {/* Added To */}
          <div className="list-group-item d-flex p-2" role="menuitem">
            <div className="selected-icon" style={{ width: '24px', flexShrink: 0 }}>
              {addedDateTo && (
                <a
                  href="#"
                  className="text-decoration-none"
                  onClick={(e) => {
                    e.preventDefault()
                    onAddedDateToChange('')
                    onDatesSet()
                  }}
                >
                  <i className="bi bi-check"></i>
                </a>
              )}
            </div>
            <div className="input-group input-group-sm ps-1 pe-2">
              <span className="input-group-text small text-muted" style={{ width: '60px' }}>
                {t('to')}
              </span>
              <Form.Control
                type="date"
                size="sm"
                value={addedDateTo}
                onChange={(e) => {
                  // Clear relative date when manual date is entered
                  if (e.target.value && addedRelativeDate) {
                    onAddedRelativeDateChange(undefined)
                  }
                  onAddedDateToChange(e.target.value)
                  onDatesSet()
                }}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      </Dropdown.Menu>
      {show && overlayContainer && createPortal(<div className="filter-panel-overlay" onClick={() => setShow(false)}></div>, overlayContainer)}
    </Dropdown>
  )
}

export default DatesDropdown
